import { isValidationError } from '@servicetitan/cp-api';
import { LoadStatus, PortalUserData } from '@servicetitan/cp-ui';
import { CheckboxFieldState, InputFieldState } from '@servicetitan/form-state';
import { Log } from '@servicetitan/log-service';
import { injectable, inject } from '@servicetitan/react-ioc';
import { AuthApi, ContactMethod, UserRegistration, UserTokenRegistration } from 'api';
import { FormState } from 'formstate';
import { computed, makeObservable, observable, runInAction } from 'mobx';
import { createPasswordValidator } from '../utils/password.validator';
import { LoginResult, PortalAuthStore } from './portal-auth.store';

@injectable()
export class AuthInviteStore {
    @observable loadStatus = LoadStatus.None;
    @observable loadError?: 'already_registered' | 'not_found';
    @observable submitStatus = LoadStatus.None;
    @observable submitResult?: LoginResult;
    @observable authorizedUser?: PortalUserData;
    @observable mapStatus = LoadStatus.None;
    @observable contactMethod?: ContactMethod;
    /*
     * @observable showMergeOption = false;
     */

    @computed get canSubmit() {
        return (
            !!this.form.$.email.value &&
            !!this.form.$.password.value &&
            !!this.form.$.confirm.value &&
            this.form.$.agree.value &&
            !this.form.hasError
        );
    }

    passwordValidator = createPasswordValidator();

    form = new FormState({
        email: new InputFieldState(''),
        password: new InputFieldState(''),
        confirm: new InputFieldState(''),
        agree: new CheckboxFieldState(false).validators(
            val => !val && 'Please agree to the terms and conditions'
        ),
    });

    private code?: string;

    constructor(
        @inject(AuthApi) private authApi: AuthApi,
        @inject(PortalAuthStore) private portalAuthStore: PortalAuthStore,
        @inject(Log) private log: Log
    ) {
        makeObservable(this);

        this.form.$.password.validators(
            value => !value.length && 'Enter a password',
            this.passwordValidator.formValidator
        );

        this.form.$.confirm.validators(
            value => !value.length && 'Enter a password',
            val => val !== this.form.$.password.value && "Passwords don't match"
        );
    }

    init = async (code: string, auto?: boolean) => {
        runInAction(() => {
            // this.showMergeOption = false;
            this.authorizedUser = undefined;
            this.loadStatus = LoadStatus.Loading;
        });

        let result;
        let user: PortalUserData | undefined;
        let isRegistered = false;

        try {
            [result, user] = await Promise.all([
                this.authApi.client.validateInvitationToken(code),
                this.portalAuthStore.validateAnyAccount(),
            ]);
        } catch (e) {
            result = undefined;
            user = undefined;

            if (
                isValidationError(e) &&
                e.errors.invitationToken?.[0] === 'Invitation has already been claimed'
            ) {
                isRegistered = true;
            }
        }

        if (!result?.contactValue) {
            runInAction(() => {
                this.loadError = isRegistered ? 'already_registered' : 'not_found';
                this.loadStatus = LoadStatus.Error;
            });

            return;
        }

        if (!auto || !user) {
            this.form.$.email.onChange(result?.contactValue ?? '');
            this.code = code;

            runInAction(() => {
                this.authorizedUser = user;
                this.contactMethod = result.contactMethod;
                // this.showMergeOption = !!user;
                this.loadStatus = LoadStatus.Ok;
            });

            return;
        }

        // await this.mapUser(code);

        runInAction(() => {
            this.authorizedUser = user;
            this.loadStatus = LoadStatus.Ok;
        });
    };

    submit = async () => {
        await this.form.validate();

        if (this.form.hasError || !this.code) {
            return;
        }

        runInAction(() => (this.submitStatus = LoadStatus.Loading));

        const { email, password } = this.form.$;

        try {
            await this.authApi.client.register(
                new UserTokenRegistration({
                    invitationToken: this.code,
                    user: new UserRegistration({
                        userName: email.value,
                        password: password.value,
                    }),
                })
            );

            const loginResult = await this.portalAuthStore.login(
                email.value,
                password.value,
                false
            );

            runInAction(() => {
                this.submitResult = loginResult;
                this.submitStatus = LoadStatus.Ok;
            });
        } catch (e) {
            let caught = false;

            if (isValidationError(e)) {
                const userError = e.errors['User.ContactValue'];

                if (userError?.length) {
                    runInAction(() => (this.submitStatus = LoadStatus.None));
                    this.form.$.email.setError(
                        this.contactMethod === ContactMethod.PhoneNumber
                            ? 'Phone number is already taken'
                            : 'Email is already taken'
                    );
                    caught = true;
                }
            }

            if (!caught) {
                const ex: any = e;
                this.log.error({
                    category: 'CP2.InviteStore.Register',
                    error: ex.response ?? ex.errors,
                });
                runInAction(() => (this.submitStatus = LoadStatus.Error));
            }
        }
    };

    mapUser = (_?: string) => {};

    /*
     * mapUser = async (code?: string) => {
     *     if (!code || !this.authorizedUser) {
     *         return;
     *     }
     *
     *
     *     runInAction(() => (this.mapStatus = LoadStatus.Loading));
     *
     *     try {
     *         await this.authApi.client.mergeExisting(new InvitationToken({ token: code }));
     *
     *         runInAction(() => (this.mapStatus = LoadStatus.Ok));
     *     } catch {
     *         runInAction(() => (this.mapStatus = LoadStatus.Error));
     *     }
     * };
     */

    // @action setShowMergeOption = (value: boolean) => (this.showMergeOption = value);
}
